import { Loader2, Trash2 } from "lucide-react";
import { useLingui } from "@lingui/react/macro";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";

export interface ConfirmDeleteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  bullets?: string[];
  note?: string;
  confirmLabel?: string;
  onConfirm: () => void;
  isPending?: boolean;
  /** Holds the confirm button back (e.g. while what it affects is loading); Cancel stays usable. */
  confirmDisabled?: boolean;
}

export function ConfirmDeleteDialog({
  open,
  onOpenChange,
  title,
  description,
  bullets,
  note,
  confirmLabel,
  onConfirm,
  isPending = false,
  confirmDisabled = false,
}: ConfirmDeleteDialogProps) {
  const { t } = useLingui();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        hideClose
        className="max-w-[500px] p-0 gap-0 overflow-hidden rounded-xl border border-(--shell-border)"
      >
        {/* Icon block */}
        <div className="flex h-[248px] items-center justify-center bg-(--color-surface-muted) border-b border-(--color-border)">
          <Trash2 className="size-20 text-(--color-danger-text)" strokeWidth={1.5} />
        </div>

        {/* Text block */}
        <div className="p-5">
          <div className="flex flex-col gap-3">
            <DialogTitle className="text-2xl font-medium text-(--color-text-primary)">
              {title}
            </DialogTitle>
            <div className="flex flex-col gap-2 text-base text-(--color-text-secondary)">
              <p>{description}</p>
              {bullets && bullets.length > 0 && (
                <ul className="list-disc pl-6 flex flex-col gap-1">
                  {bullets.map((bullet, i) => (
                    <li key={i}>{bullet}</li>
                  ))}
                </ul>
              )}
              {note && <p>{note}</p>}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 p-5">
          <Button
            variant="outline"
            className="flex-1 h-[52px] rounded-[10px] border-(--shell-border) text-base font-medium text-(--color-text-primary)"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            {t`Cancel`}
          </Button>
          <Button
            className="flex-1 h-[52px] rounded-[10px] bg-(--color-danger) hover:bg-(--color-danger)/90 text-(--brand-white) text-base font-medium"
            onClick={onConfirm}
            disabled={isPending || confirmDisabled}
          >
            {isPending && <Loader2 className="mr-2 size-4 animate-spin" />}
            {confirmLabel ?? t`Delete`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
